import {useConfig} from '@aboutyou/config';
import {Image} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/image.types';
import {
  SpotlightSection_Element,
  SpotlightSection_Media,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  BackgroundCard as BackgroundCardUI,
  COLORS,
  SPOTLIGHT_GLOWS_DESKTOP,
  SPOTLIGHT_GLOWS_MOBILE,
  SpotlightSlideAmbientBackground,
  tabletAndUpLinaria,
  useFullWidthSmallImage,
} from '@aboutyou/ui-components';
import {getColorShade} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {FC, useMemo} from 'react';
import DealBackgroundDarkLandscape from './assets/deal-background-dark-landscape.svg';
import DealBackgroundDark from './assets/deal-background-dark.svg';
import DealBackgroundLightLandscape from './assets/deal-background-light-landscape.svg';
import DealBackgroundLight from './assets/deal-background-light.svg';
import {gradientColorToCss} from './utils';

/** Image (or video poster) used for the classic blur/zoom ambient behind a slide. */
const getSlideAmbientImage = (
  media: SpotlightSection_Media | undefined,
): Image | undefined => {
  const variant = media?.variant;
  if (!variant) return undefined;
  if (variant.$case === 'image') return variant.image;
  return variant.video.fallbackImage;
};

export const BackgroundCard: FC<
  SpotlightSection_Element & {
    isActive: boolean;
  }
> = ({type, isActive}) => {
  const {device} = useConfig();
  const isMobile = device === 'mobile';
  const LightSVGImage = isMobile
    ? DealBackgroundLight
    : DealBackgroundLightLandscape;
  const DarkSVGImage = isMobile
    ? DealBackgroundDark
    : DealBackgroundDarkLandscape;

  const cardData: {
    scale: number;
    blur: number;
    color?: string;
    linearGradient?: string;
    image?: Image;
    svgImage?: string;
  } = useMemo(() => {
    const linearGradient =
      'linear-gradient(0deg, rgba(0, 0, 0, 0.00) 100%, rgba(0, 0, 0, 0.16) 0%), rgba(0, 0, 0, 0.10)';

    switch (type?.$case) {
      case 'teaser': {
        return {
          scale: 2.5,
          blur: 50,
          image: isMobile
            ? type.teaser.coverSquareImage
            : type.teaser.coverLandscapeImage,
          linearGradient,
        };
      }
      case 'campaign': {
        const colorShade = getColorShade(type.campaign.backgroundColor.hex);

        return {
          scale: isMobile ? 2.5 : 1.1,
          blur: 10,
          color: type.campaign.backgroundColor.hex,
          // we use the opposite color from the given shade to get contrast
          svgImage: colorShade === 'dark' ? LightSVGImage : DarkSVGImage,
        };
      }
      case 'highTierCoupon': {
        const colorShade = getColorShade(
          type.highTierCoupon.backgroundColor.hex,
        );

        return {
          scale: isMobile ? 2.5 : 1.1,
          blur: 10,
          color: type.highTierCoupon.backgroundColor.hex,
          // we use the opposite color from the given shade to get contrast
          svgImage: colorShade === 'dark' ? LightSVGImage : DarkSVGImage,
        };
      }
      case 'content': {
        // CMS-provided background color; falls back to the blurred cover image
        const color = isMobile
          ? type.content.backgroundColorSquare?.hex
          : type.content.backgroundColorLandscape?.hex;
        const coverImage = isMobile
          ? type.content.coverSquareImage
          : type.content.coverLandscapeImage;

        return {
          scale: 2.5,
          blur: 50,
          image: color ? undefined : coverImage,
          color,
          linearGradient,
        };
      }
      case 'slide': {
        // Media without a background colour → classic blur/zoom ambience
        // (same treatment as teaser/content). Colour / glow slides use
        // SpotlightSlideAmbientBackground instead (the early return below the
        // memo also catches media slides that carry gradient colours).
        const ambientImage = getSlideAmbientImage(type.slide.media);
        const hasBackgroundColor = Boolean(
          type.slide.colors?.backgroundColor.hex,
        );

        if (ambientImage && !hasBackgroundColor) {
          return {
            scale: 2.5,
            blur: 50,
            image: ambientImage,
            linearGradient,
          };
        }

        return {
          scale: 2.5,
          blur: 50,
          image: undefined,
          linearGradient,
        };
      }
      default:
        return {
          scale: 2.5,
          blur: 50,
          image: undefined,
          linearGradient,
        };
    }
  }, [type, isMobile, LightSVGImage, DarkSVGImage]);

  const backgroundImageUrl = useFullWidthSmallImage({
    ratio: '1:1',
    src: cardData && cardData.image ? cardData.image.url : '',
  });

  if (type?.$case === 'slide') {
    const backgroundColorHex = type.slide.colors?.backgroundColor.hex;
    const hasMedia = Boolean(type.slide.media?.variant);
    const glowColors = type.slide.gradientColors.map(gradientColorToCss);
    const hasGlow = glowColors.length > 0;

    // Ambient glows render whenever the backend sends gradient colours, media
    // or not (Figma "Image=true" variants keep the glowing background behind
    // the photo card). Only a media slide with neither colour nor glow falls
    // through to the classic BlurryBackground (blur + zoom).
    const useBlurryAmbient = hasMedia && !backgroundColorHex && !hasGlow;

    if (!useBlurryAmbient) {
      return (
        <SpotlightSlideAmbientBackground
          isActive={isActive}
          backgroundColor={backgroundColorHex ?? COLORS['Fill.Black']}
          glowColors={hasGlow ? glowColors : undefined}
          glows={isMobile ? SPOTLIGHT_GLOWS_MOBILE : SPOTLIGHT_GLOWS_DESKTOP}
        />
      );
    }
  }

  const backgroundColor =
    cardData.color ??
    (backgroundImageUrl === '' ? COLORS['Fill.Black'] : undefined);

  return (
    <StyledBackgroundCardUI
      isActive={isActive}
      imageUrl={cardData.svgImage ? cardData.svgImage : backgroundImageUrl}
      color={backgroundColor}
      linearGradient={cardData?.linearGradient}
      blur={cardData.blur}
      scale={cardData.scale}
    />
  );
};

const StyledBackgroundCardUI = styled(BackgroundCardUI)`
  ${tabletAndUpLinaria(`
    /* background-size targets the BlurryBackground child via the cascade on
       the clipped Root — keep cover on desktop like legacy. */
    & > * {
      background-size: cover;
    }
  `)}

  /* scrim lives on the fading BlurryBackground child (not the always-visible
     li), so it crossfades with the slide instead of permanently darkening
     whichever ambient is active. */
  & > *::after {
    content: '';
    background: rgba(0, 0, 0, 0.2);
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
`;
